// src/screens/AcceptPlayerInvite.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { CheckCircle2, LogIn, Loader2, AlertTriangle, UserPlus } from "lucide-react";
import i18n from "../i18n";

/* ========= i18n mini-hook (fallback string support) ========= */
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown> | string): string => {
    if (typeof options === "string") {
      const translated = (i18n.t as any)(key);
      if (translated === key) return options;
      return String(translated);
    }
    return String((i18n.t as any)(key, options));
  };
  return { t, lng };
}

type RpcRow = {
  team_id: string;
  player_json_id: string | null;
  player_number: number | null;
};

export default function AcceptPlayerInvite() {
  const { t } = useI18nSafe();
  const { token: rawToken } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<"loading" | "need-auth" | "done" | "error">("loading");
  const [message, setMessage] = useState<string>("");
  const [nextPath, setNextPath] = useState<string | null>(null);

  useEffect(() => {
    // --- Empêche la double exécution en dev (StrictMode) ---
    const didRunRef = (AcceptPlayerInvite as any).__didRun ?? { current: false };
    (AcceptPlayerInvite as any).__didRun = didRunRef;
    if (didRunRef.current) return;
    didRunRef.current = true;

    let isMounted = true;

    (async () => {
      try {
        if (!rawToken) {
          if (!isMounted) return;
          setState("error");
          setMessage(t("acceptinvite:errors.invalid_link", "Lien invalide."));
          return;
        }

        const token = decodeURIComponent(rawToken);

        const { data: auth } = await supabase.auth.getUser();
        if (!auth?.user) {
          if (!isMounted) return;
          setState("need-auth");
          return;
        }

        // 1) tentative maybeSingle (évite 406)
        const { data: singleRow, error: singleErr } = await supabase
          .rpc("accept_player_invite", { p_token: token })
          .maybeSingle<RpcRow>();

        if (singleErr) {
          console.error("[accept][rpc][single] error:", singleErr);
          if (!isMounted) return;
          setState("error");
          setMessage(t("acceptinvite:error_card.generic", "Une erreur est survenue."));
          return;
        }

        let row: RpcRow | null = singleRow ?? null;

        // 2) fallback si la fonction retourne un SETOF
        if (!row) {
          const { data: arr, error: arrErr } = await supabase.rpc("accept_player_invite", { p_token: token });
          if (arrErr) {
            console.error("[accept][rpc][array] error:", arrErr);
            if (!isMounted) return;
            setState("error");
            setMessage(t("acceptinvite:error_card.generic", "Une erreur est survenue."));
            return;
          }
          row = Array.isArray(arr) ? (arr[0] as RpcRow) : (arr as RpcRow | null);
        }

        // 3) décision finale
        if (!row?.team_id) {
          if (!isMounted) return;
          setState("error");
          setMessage(t("acceptinvite:errors.invite_not_found", "Invitation introuvable ou expirée."));
          return;
        }

        // Succès : choisis la destination ici
        setNextPath("/dashboard");
        // Ou vers la page player :
        // const suffix = row.player_number != null ? `/${row.player_number}` : "";
        // setNextPath(`/player/${row.team_id}${suffix}`);

        if (!isMounted) return;
        setState("done");
      } catch (err) {
        console.error("[accept][rpc] exception:", err);
        if (!isMounted) return;
        setState("error");
        setMessage(t("acceptinvite:error_card.generic", "Une erreur est survenue."));
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawToken, t]);

  // Redirection déclenchée proprement quand l'état passe à "done"
  useEffect(() => {
    if (state !== "done" || !nextPath) return;
    const tid = setTimeout(() => navigate(nextPath, { replace: true }), 900);
    return () => clearTimeout(tid);
  }, [state, nextPath, navigate]);

  if (state === "loading") {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950">
        <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  if (state === "need-auth") {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950 p-6">
        <div className="max-w-sm w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 text-center">
          <LogIn className="w-10 h-10 mx-auto mb-3 text-orange-600" />
          <h2 className="text-lg font-semibold mb-2">
            {t("acceptinvite:auth.title", "Connexion requise")}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
            {t("acceptinvite:auth.subtitle", "Connecte-toi ou crée un compte pour accepter l’invitation.")}
          </p>

          <div className="flex justify-center gap-3">
            <Link
              to={`/login?redirect=${encodeURIComponent(`/accept-player-invite/${rawToken ?? ""}`)}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition"
            >
              <LogIn className="w-4 h-4" />
              {t("acceptinvite:auth.login_btn", "Se connecter")}
            </Link>

            <Link
              to={`/signup?redirect=${encodeURIComponent(`/accept-player-invite/${rawToken ?? ""}`)}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            >
              <UserPlus className="w-4 h-4" />
              {t("acceptinvite:auth.signup_btn", "Créer un compte")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950 p-6">
        <div className="max-w-sm w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
          <h2 className="text-lg font-semibold mb-2">{t("acceptinvite:error_card.title", "Erreur")}</h2>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {message || t("acceptinvite:error_card.generic", "Une erreur est survenue.")}
          </p>
          <div className="mt-5">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
            >
              {t("acceptinvite:error_card.back_home", "Retour à l’accueil")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div className="min-h-screen grid place-items-center bg-gray-50 dark:bg-gray-950 p-6">
        <div className="max-w-sm w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 text-center">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-600" />
          <h2 className="text-lg font-semibold mb-2">
            {t("acceptinvite:success.title", "Invitation acceptée ✅")}
          </h2>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t("acceptinvite:success.redirecting", "Redirection en cours…")}
          </p>
        </div>
      </div>
    );
  }

  return null;
}
